import React from 'react'
import { Company } from 'models'
import { companiesActions, type CompaniesReducerAction } from '../../reducer'
import type { CompaniesReducerState } from '../../types'
import { isAllAccountsOption, buildSelectionState } from '../../helpers/selection'
import { MAX_SELECTED_CHILDREN } from '../../constants'

const useCompanySelectionActions = (params: {
  state: CompaniesReducerState
  dispatch: React.Dispatch<CompaniesReducerAction>
  selectableChildren: Array<Company>
  selectionLimit: number
  onResetWaitingFiles: () => void
}) => {
  const { state, dispatch, selectableChildren, selectionLimit, onResetWaitingFiles } = params
  const {
    isOrphanSelected,
    pendingSelection,
    pendingAllAccountsSelection,
    selectedChildren,
    isAllAccountsSelected,
  } = state

  const setPendingSelection = React.useCallback(
    (companies: Array<Company>, options?: { isAllAccountsSelected?: boolean }) => {
      const selectionWithoutAllAccounts = companies
        .filter((company) => !isAllAccountsOption(company))
        .slice(0, selectionLimit)

      const requestsAllAccounts = Boolean(
        options?.isAllAccountsSelected || companies.some(isAllAccountsOption),
      )

      const canUseAllAccounts =
        requestsAllAccounts &&
        !isOrphanSelected &&
        selectableChildren.length > 0 &&
        selectableChildren.length <= MAX_SELECTED_CHILDREN

      const nextSelection = canUseAllAccounts ? selectableChildren : selectionWithoutAllAccounts

      dispatch({
        type: companiesActions.SET_PENDING_SELECTION,
        companies: nextSelection,
        isAllAccountsSelected: canUseAllAccounts,
      })
      onResetWaitingFiles()
    },
    [dispatch, isOrphanSelected, onResetWaitingFiles, selectableChildren, selectionLimit],
  )

  const validateSelection = React.useCallback(
    (options?: { selection?: Array<Company>; isAllAccountsSelected?: boolean }) => {
      const nextSelection = options?.selection ?? pendingSelection
      const explicitAllAccountsFlag = options?.isAllAccountsSelected
      const nextIsAllAccountsSelected =
        typeof explicitAllAccountsFlag === 'boolean'
          ? explicitAllAccountsFlag && Boolean(nextSelection.length)
          : pendingAllAccountsSelection && Boolean(nextSelection.length)

      dispatch({
        type: companiesActions.APPLY_SELECTION,
        companies: nextSelection,
        isAllAccountsSelected: nextIsAllAccountsSelected,
      })
      onResetWaitingFiles()
    },
    [dispatch, onResetWaitingFiles, pendingAllAccountsSelection, pendingSelection],
  )

  const pendingSelectionState = React.useMemo(
    () =>
      buildSelectionState({
        selection: pendingSelection,
        isAllAccounts: pendingAllAccountsSelection && Boolean(pendingSelection.length),
      }),
    [pendingAllAccountsSelection, pendingSelection],
  )

  const validatedSelectionState = React.useMemo(
    () =>
      buildSelectionState({ selection: selectedChildren, isAllAccounts: isAllAccountsSelected }),
    [isAllAccountsSelected, selectedChildren],
  )

  return {
    setPendingSelection,
    validateSelection,
    pendingSelectionState,
    validatedSelectionState,
    pendingSelectionIsAllAccounts: pendingSelectionState.mode === 'allAccounts',
  }
}

export { useCompanySelectionActions }
